import { useCallback, useEffect, useRef, useState } from "react";
import { useEscapeLayer } from "./layers.js";
import { portalContainer } from "./portal.js";
/**
 * A floating panel anchored to `anchor`: placed below (or above when there is no room), kept in the viewport,
 * closed on an outside pointer-down and by Esc when it is the topmost layer. Rendered into `portalContainer` of the anchor.
 */
export function usePopover(anchor, { minWidth = 220, height = 340, onClose, fit = false, capture = false } = {}) {
    const [position, setPosition] = useState(null);
    const panel = useRef(null);
    const closeRef = useRef(onClose);
    closeRef.current = onClose;
    const locate = useCallback(() => {
        const element = anchor.current;
        if (!element)
            return;
        const rect = element.getBoundingClientRect();
        const measured = fit && panel.current ? panel.current.offsetHeight : 0;
        const need = measured || height;
        const width = Math.min(Math.max(fit ? minWidth : rect.width, minWidth), window.innerWidth - 16);
        // A content-sized panel is placed by its drawn width once it exists: placed by the minimum, a wider one (a long menu item)
        // stuck out past the right edge under a button at the bar's end (facts, help button in the bar; quint facts_look Лв6).
        const drawn = fit && panel.current ? Math.min(Math.max(width, panel.current.offsetWidth), window.innerWidth - 16) : width;
        const left = Math.max(8, Math.min(rect.left, window.innerWidth - drawn - 8));
        const up = rect.bottom + need > window.innerHeight && rect.top > need;
        const container = portalContainer(element);
        // A theme set on a wrapper (not on <html>) must follow the panel into the portal.
        const themed = element.closest("[data-theme]");
        const theme = themed && themed !== document.documentElement ? themed.getAttribute("data-theme") ?? undefined : undefined;
        // So must a density set on a wrapper.
        const dense = element.closest("[data-density]");
        const density = dense && dense !== document.documentElement ? dense.getAttribute("data-density") ?? undefined : undefined;
        setPosition(up ? { left, bottom: window.innerHeight - rect.top + 4, width, container, theme, density } : { left, top: rect.bottom + 4, width, container, theme, density });
    }, [anchor, minWidth, height, fit]);
    const close = useCallback((focusAnchor = true) => {
        setPosition(null);
        closeRef.current?.();
        if (focusAnchor)
            anchor.current?.focus();
    }, [anchor]);
    /** Drop the panel without calling onClose (the host already knows). */
    const hide = useCallback(() => setPosition(null), []);
    const open = position !== null;
    useEscapeLayer(open, () => close());
    useEffect(() => {
        if (!open)
            return;
        const outside = (event) => {
            const target = event.target;
            if (!panel.current?.contains(target) && !anchor.current?.contains(target))
                close(false);
        };
        document.addEventListener("pointerdown", outside, capture);
        window.addEventListener("resize", locate);
        window.addEventListener("scroll", locate, true);
        return () => {
            document.removeEventListener("pointerdown", outside, capture);
            window.removeEventListener("resize", locate);
            window.removeEventListener("scroll", locate, true);
        };
    }, [open, anchor, close, locate, capture]);
    const style = position
        ? { left: position.left, ...(fit ? { minWidth: position.width } : { width: position.width }), ...(position.top !== undefined ? { top: position.top } : { bottom: position.bottom }) }
        : undefined;
    return { open, position, style, panel, show: locate, close, hide };
}
